import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, Video, CheckCircle, ArrowRight, Loader2, FileText, Users, PlayCircle } from 'lucide-react';
import PortalLayout from './PortalLayout';
import UpcomingBookings from '../../components/portal/UpcomingBookings';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const muted = { color: 'var(--p-muted)' };

export default function ClientSessions() {
  const [sessions, setSessions] = useState([]);
  const [groupSessions, setGroupSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/portal/sessions`, { withCredentials: true })
        .then((res) => setSessions(Array.isArray(res.data) ? res.data : [])).catch(() => setSessions([])),
      axios.get(`${API_URL}/api/portal/group-sessions`, { withCredentials: true })
        .then((res) => setGroupSessions(Array.isArray(res.data) ? res.data : [])).catch(() => setGroupSessions([])),
    ]).finally(() => setLoading(false));
  }, []);

  const upcoming = sessions.filter((s) => s.status !== 'completed');
  const past = sessions.filter((s) => s.status === 'completed');

  const hasNotes = (s) => s.ai_summary || (s.ai_key_takeaways || []).length || s.notes;

  return (
    <PortalLayout active="sessions">
      <div className="mb-8" data-testid="client-sessions-page">
        <span className="text-xs tracking-[0.2em] uppercase text-[#F97316] font-semibold p-eyebrow">1-on-1 Coaching</span>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-light mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="sessions-title">
          My Sessions
        </h1>
        <p className="mt-2 max-w-2xl" style={muted}>Join your booked strategy sessions and review your AI-generated recap, action plan and transcript after each call.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1D4ED8]" size={28} /></div>
      ) : (
        <>
          <UpcomingBookings />
          {/* Upcoming */}
          <section className="mb-10">
            <h2 className="font-semibold mb-4 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Upcoming</h2>
            {upcoming.length > 0 ? (
              <div className="space-y-4" data-testid="upcoming-sessions">
                {upcoming.map((s, i) => (
                  <Link
                    key={s.session_id}
                    to={`/portal/sessions/${s.session_id}`}
                    className="p-card rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:-translate-y-0.5 transition-transform"
                    data-testid={`upcoming-session-${i}`}
                  >
                    <div>
                      <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm" style={muted}>
                        <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#1D4ED8]" />{s.session_date || 'TBD'}</span>
                        {s.session_time && <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#1D4ED8]" />{s.session_time}</span>}
                        <span className="flex items-center gap-1.5"><Video size={14} className="text-[#1D4ED8]" />{(s.session_type || 'one on one').replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap" style={{ background: 'rgba(29,78,216,0.12)', color: '#1D4ED8' }}>{s.duration || '60 min'}</span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1D4ED8]">Open <ArrowRight size={15} /></span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-card rounded-2xl p-8 text-center" data-testid="no-upcoming-sessions">
                <Calendar size={36} className="mx-auto mb-3" style={{ color: 'var(--p-faint)' }} />
                <p className="text-sm mb-4" style={muted}>No upcoming sessions scheduled yet.</p>
                <Link to="/portal/book" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 rounded-lg font-medium text-sm transition-colors" data-testid="book-session-cta">
                  Book a session <ArrowRight size={15} />
                </Link>
              </div>
            )}
          </section>

          {/* Past */}
          {past.length > 0 && (
            <section data-testid="past-sessions">
              <h2 className="font-semibold mb-4 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Past sessions &amp; recaps</h2>
              <div className="space-y-3">
                {past.map((s, i) => (
                  <Link
                    key={s.session_id}
                    to={`/portal/sessions/${s.session_id}`}
                    className="p-card rounded-xl p-4 flex items-center gap-4 hover:-translate-y-0.5 transition-transform"
                    data-testid={`past-session-${i}`}
                  >
                    <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
                    <div className="flex-grow min-w-0">
                      <div className="font-medium truncate" style={{ color: 'var(--p-text)' }}>{s.title}</div>
                      <div className="text-xs" style={muted}>{s.session_date}</div>
                    </div>
                    {hasNotes(s) && (
                      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: 'rgba(16,185,129,0.12)', color: '#059669' }}>
                        <FileText size={12} /> Recap ready
                      </span>
                    )}
                    <ArrowRight size={16} style={{ color: 'var(--p-faint)' }} />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Group sessions, member-wide */}
          <section className="mt-12" data-testid="group-sessions-section">
            <div className="flex items-center gap-2 mb-1">
              <Users size={18} className="text-[#F97316]" />
              <h2 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Group Sessions</h2>
            </div>
            <p className="text-sm mb-4" style={muted}>Member-wide group calls: recordings, transcripts, recaps, action plans &amp; goals, open to everyone.</p>
            {groupSessions.length > 0 ? (
              <div className="space-y-3" data-testid="group-sessions-list">
                {groupSessions.map((g, i) => {
                  const done = g.status === 'completed';
                  const hasRecap = g.ai_summary || (g.ai_key_takeaways || []).length || g.recording_url || g.notes;
                  return (
                    <Link
                      key={g.group_session_id}
                      to={`/portal/group-sessions/${g.group_session_id}`}
                      className="p-card rounded-xl p-4 flex items-center gap-4 hover:-translate-y-0.5 transition-transform"
                      data-testid={`group-session-${i}`}
                    >
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(249,115,22,0.12)', color: '#F97316' }}>
                        {done ? <PlayCircle size={18} /> : <Users size={18} />}
                      </span>
                      <div className="flex-grow min-w-0">
                        <div className="font-medium truncate" style={{ color: 'var(--p-text)' }}>{g.title}</div>
                        <div className="text-xs flex items-center gap-2" style={muted}>
                          {g.session_date && <span>{g.session_date}</span>}
                          {g.session_time && <span>· {g.session_time}</span>}
                        </div>
                      </div>
                      {g.recording_url && (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: 'rgba(29,78,216,0.12)', color: '#1D4ED8' }}>
                          <PlayCircle size={12} /> Recording
                        </span>
                      )}
                      {hasRecap && done && (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: 'rgba(16,185,129,0.12)', color: '#059669' }}>
                          <FileText size={12} /> Recap ready
                        </span>
                      )}
                      <ArrowRight size={16} style={{ color: 'var(--p-faint)' }} />
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="p-card rounded-2xl p-8 text-center" data-testid="no-group-sessions">
                <Users size={32} className="mx-auto mb-3" style={{ color: 'var(--p-faint)' }} />
                <p className="text-sm" style={muted}>No group sessions yet. When David hosts a group call, the recording and recap will appear here for all members.</p>
              </div>
            )}
          </section>
        </>
      )}
    </PortalLayout>
  );
}
